import { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import { cancelDelayUntilIdle, delayUntilIdle } from '../helpers/delayUntilIdle';
import { useGetAssociatedObjectsPagedQuery } from '../../__generated__/chirp/com/hubspot/card/associated/objects/rpc/AssociatedObjectsGatewayRpc.hooks';
import { getObjectFromContext } from '../context/getObjectsFromContext';
import { TOPIC_NAMES } from 'crm-message-bus/types/MessageTopics';
import { useSendCrmMessageTopic } from 'crm-message-bus/useSendCrmMessageTopic';
import { normalizeTypeId } from 'customer-data-objects/constants/ObjectTypeIds';
import isPortalSpecificObjectType from 'customer-data-objects/crmObject/isPortalSpecificObjectType';
import { CrmLogger } from 'customer-data-tracking/loggers';
import { useGetHasFeature } from 'crm-cards-sdk/hooks/useGetHasFeature';
import { useGetIsUngated } from 'crm-cards-sdk/hooks/useGetIsUngated';
import { useGetIsScoped } from 'crm-cards-sdk/hooks/useGetIsScoped';
import Raven from 'raven-js';
import { getRequiredPropertyNames } from '../constants/RequiredPropertyNames';
import { useUserCustomization } from './useUserCustomization';
import { CRM_FEATURES } from 'crm-cards-sdk/types/CrmFeatures';
import { useOnCrmMessageTopic } from 'crm-message-bus/useOnCrmMessageTopic';
import { usageTracker } from '../utils/usageTracker';
import { createSortConfiguration } from '../utils/createSortConfiguration';
export const POLL_INTERVAL = 500;
export const UNKNOWN_ACTION = 'UNKNOWN';
export const MAX_POLL_TIME = 6000;
// Num of associations to fetch initially and for each infinite scroll fetch
// 12 because with css grid, max we should is 2 rows x 4 columns, with a possible 3rd row showing dependong on # of properties
export const PAGE_LENGTH = 12;
export const useFetchAssociatedObjectsCardRedesignData = (props, cardContext) => {
  const {
    isCollapsed
  } = props.cardData;
  const [shouldDeferDataRequests, setShouldDeferDataRequests] = useState(Boolean(isCollapsed));
  const [total, setTotal] = useState();
  const sortConfiguration = props.cardData.configuration.sortConfiguration || [];
  const initialSort = sortConfiguration[0];
  const initialSortConfiguration = createSortConfiguration(initialSort === null || initialSort === void 0 ? void 0 : initialSort.property, initialSort === null || initialSort === void 0 ? void 0 : initialSort.direction);
  const [localSortConfiguration, setLocalSortConfiguration] = useState(initialSortConfiguration);
  const isFetchingMoreRef = useRef(false);
  const updateAssociationAction = useRef();
  const timerRef = useRef();
  useEffect(() => {
    if (!isCollapsed) {
      setShouldDeferDataRequests(false);
      return;
    }
    const idleCallbackId = delayUntilIdle(() => {
      setShouldDeferDataRequests(false);
    });
    return () => {
      if (idleCallbackId !== undefined) {
        cancelDelayUntilIdle(idleCallbackId);
      }
    };
  }, [isCollapsed]);
  const {
    objectTypeId,
    objectId
  } = getObjectFromContext(cardContext);
  const {
    toObjectTypeId
  } = props.cardData.configuration;
  const isUngated = useGetIsUngated();
  const hasScope = useGetIsScoped();
  const hasFeature = useGetHasFeature();
  const {
    updateSortConfiguration
  } = useUserCustomization({
    cardId: props.cardData.definition.cardId,
    objectTypeId
  });
  const requiredPropertyNames = getRequiredPropertyNames(toObjectTypeId, props.cardData.configuration.propertyNames);
  const {
    data,
    loading,
    error,
    refetch,
    fetchMore,
    variables,
    previousData,
    stopPolling,
    startPolling
  } = useGetAssociatedObjectsPagedQuery({
    variables: {
      fromObjectTypeId: objectTypeId,
      fromObjectId: objectId,
      toObjectTypeId,
      offset: 0,
      pageLength: PAGE_LENGTH,
      isPrimaryFirst: true,
      sorts: initialSortConfiguration,
      query: '',
      propertyNames: requiredPropertyNames
    },
    fetchPolicy: 'cache-and-network'
  });
  useEffect(() => {
    if ((data === null || data === void 0 ? void 0 : data.total) !== undefined) {
      setTotal(data.total);
    }
  }, [data === null || data === void 0 ? void 0 : data.total]);
  const handleStopPolling = useCallback(() => {
    updateAssociationAction.current = undefined;
    clearTimeout(timerRef.current);
    timerRef.current = undefined;
    stopPolling();
  }, [stopPolling]);
  const handleStartPolling = useCallback(action => {
    updateAssociationAction.current = action || UNKNOWN_ACTION;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    startPolling(POLL_INTERVAL);
    timerRef.current = setTimeout(() => {
      if (updateAssociationAction.current) {
        handleStopPolling();
      }
    }, MAX_POLL_TIME);
  }, [startPolling, handleStopPolling]);
  useEffect(() => {
    if (data && previousData) {
      var _previousData$result;
      const currentTotal = data.total || 0;
      const previousTotal = ((_previousData$result = previousData.result) === null || _previousData$result === void 0 ? void 0 : _previousData$result.total) || 0;
      const difOfTotal = Math.abs(previousTotal - currentTotal);
      const isPollingAndDataChanged = difOfTotal > 0;
      if (!isPollingAndDataChanged) {
        return;
      }

      // We can only remove 1 association at a time when removing so we can stop polling
      if (updateAssociationAction.current === 'REMOVE_ASSOCIATION') {
        handleStopPolling();
      }
    }
  }, [data, previousData, handleStopPolling]);
  useEffect(() => {
    return () => {
      // Cleanup polling on unmount
      handleStopPolling();
    };
  }, [handleStopPolling]);
  const [scrollContainerRef, setScrollContainerRef] = useState(undefined);
  const resetAndRefetch = useCallback(({
    additionalVariables = {},
    shouldResetScroll = false
  } = {}) => {
    if (shouldResetScroll && scrollContainerRef !== null && scrollContainerRef !== void 0 && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
    return refetch(Object.assign({
      offset: 0
    }, additionalVariables));
  }, [refetch, scrollContainerRef]);
  const handleViewMore = useCallback(() => {
    if (isFetchingMoreRef.current || !data) {
      return;
    }
    isFetchingMoreRef.current = true;
    usageTracker.track('CRM', 'Record Interactions', {
      type: 'Association Card',
      action: 'Scrolled To Load More Associations',
      toObjectTypeId,
      fromObjectTypeId: objectTypeId
    });
    fetchMore({
      variables: {
        offset: data.offset,
        pageLength: PAGE_LENGTH
      },
      updateQuery: (prev, {
        fetchMoreResult
      }) => {
        var _prev$result, _fetchMoreResult$resu;
        if (!fetchMoreResult) {
          return prev;
        }
        const existingResults = (prev === null || prev === void 0 || (_prev$result = prev.result) === null || _prev$result === void 0 ? void 0 : _prev$result.results) || [];
        const newResults = ((_fetchMoreResult$resu = fetchMoreResult.result) === null || _fetchMoreResult$resu === void 0 ? void 0 : _fetchMoreResult$resu.results) || [];
        const combinedResults = [...existingResults, ...newResults];
        return Object.assign({}, prev, {
          result: Object.assign({}, prev.result, fetchMoreResult.result, {
            results: combinedResults
          })
        });
      }
    }).catch(err => {
      Raven.captureException(err);
    }).finally(() => {
      isFetchingMoreRef.current = false;
    });
  }, [fetchMore, data, toObjectTypeId, objectTypeId]);
  useOnCrmMessageTopic(TOPIC_NAMES.UPDATE_ASSOCIATIONS, ({
    toObjectTypeId: messageToObjectTypeId,
    objectTypeId: messageObjectTypeId,
    objectId: messageObjectId,
    actionType
  }) => {
    var _data$results;
    const associatedObjectsIds = (data === null || data === void 0 || (_data$results = data.results) === null || _data$results === void 0 ? void 0 : _data$results.map(result => result.objectId)) || [];
    const matchesFromObjectCoordinatesAndToObjectTypeId = objectId === +messageObjectId && messageObjectTypeId === objectTypeId && (messageToObjectTypeId === toObjectTypeId || !messageToObjectTypeId);
    const matchesAnAssociation = associatedObjectsIds.includes(+messageObjectId) && messageObjectTypeId === toObjectTypeId;
    if (matchesFromObjectCoordinatesAndToObjectTypeId || matchesAnAssociation) {
      if (actionType === 'UPDATE_LABELS') {
        resetAndRefetch().catch(err => {
          Raven.captureException(err);
        });
      } else {
        handleStartPolling(actionType);
      }
    }
  });
  useOnCrmMessageTopic(TOPIC_NAMES.UPDATE_OBJECTS, objectData => {
    for (const object of objectData) {
      if (toObjectTypeId !== object.objectTypeId) {
        continue;
      }
      const matchingAssociation = data === null || data === void 0 ? void 0 : data.results.find(association => association.objectId === +object.objectId);
      if (!matchingAssociation) {
        continue;
      }
      resetAndRefetch().catch(err => {
        Raven.captureException(err);
      });
      break;
    }
  });
  const sendPreviewObjectMessage = useSendCrmMessageTopic(TOPIC_NAMES.PREVIEW_OBJECT);
  const setPreviewConfig = useCallback(config => {
    if (config) {
      const normalizedToObjectTypeId = normalizeTypeId(config.objectTypeId);
      const normalizedFromObjectTypeId = normalizeTypeId(objectTypeId);
      CrmLogger.log('recordInteractions', {
        toObjectTypeId: isPortalSpecificObjectType(normalizedToObjectTypeId) ? 'CUSTOM_OBJECT' : normalizedToObjectTypeId,
        fromObjectTypeId: isPortalSpecificObjectType(normalizedFromObjectTypeId) ? 'CUSTOM_OBJECT' : normalizedFromObjectTypeId,
        action: 'Clicked preview button'
      });
      sendPreviewObjectMessage({
        objectTypeId: normalizeTypeId(config.objectTypeId),
        objectId: `${config.objectId}`
      });
    }
  }, [objectTypeId, sendPreviewObjectMessage]);
  const sendOpenActivityCreator = useSendCrmMessageTopic(TOPIC_NAMES.OPEN_ACTIVITY_CREATOR);
  const sendOpenCallWidget = useSendCrmMessageTopic(TOPIC_NAMES.OPEN_CALL_WIDGET);
  const sendUpdateCardConfiguration = useSendCrmMessageTopic(TOPIC_NAMES.UPDATE_CARD_CONFIGURATION);
  const handleSearchQueryChange = useCallback(query => {
    const trimmedQuery = query.trim();
    if (trimmedQuery !== '') {
      usageTracker.track('CRM', 'Record Interactions', {
        type: 'Association Card',
        action: 'Changed Search Query',
        subAction: `user searched for ${trimmedQuery}`,
        toObjectTypeId,
        fromObjectTypeId: objectTypeId
      });
    }
    resetAndRefetch({
      additionalVariables: {
        query: trimmedQuery,
        isPrimaryFirst: trimmedQuery === ''
      },
      shouldResetScroll: true
    }).catch(err => {
      Raven.captureException(err);
    });
  }, [resetAndRefetch, toObjectTypeId, objectTypeId]);
  const handleSortConfigurationChange = useCallback(newSortConfiguration => {
    const primarySort = newSortConfiguration[0];
    const standardizedSorts = createSortConfiguration(primarySort.property, primarySort.order);
    setLocalSortConfiguration(standardizedSorts);
    resetAndRefetch({
      additionalVariables: {
        sorts: standardizedSorts
      },
      shouldResetScroll: true
    }).catch(err => {
      Raven.captureException(err);
    });
    const userSortConfig = standardizedSorts.map(sort => ({
      property: sort.property,
      // Type assertion needed as we have two non-identical 'order' types (GQL and CHIRP) which represent the same value
      direction: sort.order
    }));
    usageTracker.track('CRM', 'Record Interactions', {
      type: 'Association Card',
      action: 'Changed Sort Configuration',
      subAction: `to ${userSortConfig[0].property} ${userSortConfig[0].direction}`,
      toObjectTypeId,
      fromObjectTypeId: objectTypeId
    });
    updateSortConfiguration(userSortConfig).then(() => {
      sendUpdateCardConfiguration({
        cardId: props.cardData.definition.cardId,
        cardType: props.cardData.definition.cardType
      });
    }).catch(() => {
      // Do nothing: Error logging handled in useUserCustomization.ts
    });
  }, [resetAndRefetch, updateSortConfiguration, sendUpdateCardConfiguration, props.cardData.definition.cardId, props.cardData.definition.cardType, objectTypeId, toObjectTypeId]);
  const handleEmailClick = useCallback((recipientObjectTypeId, recipientId) => {
    if (!hasFeature(CRM_FEATURES.COMMUNICATOR_EMAIL)) {
      return;
    }
    sendOpenActivityCreator({
      interactionType: 'email',
      objectId: Number(recipientId),
      objectTypeId: recipientObjectTypeId
    });
  }, [hasFeature, sendOpenActivityCreator]);
  const handlePhoneClick = useCallback((recipientObjectTypeId, recipientId) => {
    if (!hasFeature(CRM_FEATURES.COMMUNICATOR_CALLING)) {
      return;
    }
    sendOpenCallWidget({
      interactionType: 'call',
      objectId: Number(recipientId),
      objectTypeId: recipientObjectTypeId
    });
  }, [sendOpenCallWidget, hasFeature]);
  return useMemo(() => {
    var _variables$query;
    const contextValue = {
      associationDefinition: data === null || data === void 0 ? void 0 : data.associationDefinition,
      cardId: props.cardData.definition.cardId,
      error,
      currentSearchQuery: (_variables$query = variables === null || variables === void 0 ? void 0 : variables.query) !== null && _variables$query !== void 0 ? _variables$query : '',
      fromObjectId: objectId,
      fromObjectTypeId: objectTypeId,
      hasMore: data === null || data === void 0 ? void 0 : data.hasMore,
      hasScope,
      isLoading: shouldDeferDataRequests || loading,
      isUngated,
      objectPermissions: data === null || data === void 0 ? void 0 : data.objectPermissions,
      objectTypeDefinition: data === null || data === void 0 ? void 0 : data.objectTypeDefinition,
      offset: data === null || data === void 0 ? void 0 : data.offset,
      onEmailClick: handleEmailClick,
      onPhoneClick: handlePhoneClick,
      onPreview: setPreviewConfig,
      onRefetchLegacySidebar: () => {},
      onSearchQueryChange: handleSearchQueryChange,
      onSortConfigurationChange: handleSortConfigurationChange,
      onViewMore: handleViewMore,
      propertyDefinitions: data === null || data === void 0 ? void 0 : data.propertyDefinitions,
      propertyNames: props.cardData.configuration.propertyNames,
      readOnly: Boolean(cardContext.readOnly),
      refetch,
      results: data === null || data === void 0 ? void 0 : data.results,
      setScrollContainerRef,
      sortConfiguration: localSortConfiguration,
      title: props.cardData.definition.cardTitle,
      toObjectTypeId,
      total,
      usageTracker: props.usageTracker,
      userPermission: props.cardData.configuration.userPermission
    };
    return contextValue;
  }, [data === null || data === void 0 ? void 0 : data.associationDefinition, error, data === null || data === void 0 ? void 0 : data.hasMore, data === null || data === void 0 ? void 0 : data.objectPermissions, data === null || data === void 0 ? void 0 : data.offset, data === null || data === void 0 ? void 0 : data.objectTypeDefinition, data === null || data === void 0 ? void 0 : data.propertyDefinitions, data === null || data === void 0 ? void 0 : data.results, props.cardData.definition.cardId, props.cardData.definition.cardTitle, props.cardData.configuration.propertyNames, props.cardData.configuration.userPermission, props.usageTracker, hasScope, shouldDeferDataRequests, loading, isUngated, objectId, objectTypeId, handleViewMore, handleEmailClick, handlePhoneClick, setPreviewConfig, setScrollContainerRef, handleSearchQueryChange, handleSortConfigurationChange, cardContext.readOnly, refetch, localSortConfiguration, toObjectTypeId, total, variables === null || variables === void 0 ? void 0 : variables.query]);
};